import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../../services/api';
import { removeToken } from '../../services/config';
import LoadingSpinner from '../Common/LoadingSpinner';
import './ResetPassword.css';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);

  let token = searchParams.get('token');
  
  // Decodificar o token se estiver codificado para URL (pode conter caracteres especiais)
  if (token) {
    token = decodeURIComponent(token);
  }

  useEffect(() => {
    // Verificar se o token está presente
    if (!token) {
      setTokenValid(false);
      setMessage('Token de redefinição não encontrado.');
      setIsError(true);
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      setMessage('Token inválido. Solicite uma nova redefinição de senha.');
      setIsError(true);
      return;
    }

    if (password !== confirmPassword) {
      setMessage('As senhas não coincidem.');
      setIsError(true);
      return;
    }

    if (password.length < 8) {
      setMessage('A senha deve ter pelo menos 8 caracteres.');
      setIsError(true);
      return;
    }

    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      setMessage('A senha deve conter letras maiúsculas, minúsculas e números.');
      setIsError(true);
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {

      const response = await apiService.post<{ success: boolean; message?: string }>('RESET_PASSWORD', {
        token,
        nova_senha: password  // Backend espera snake_case
      });

      if (response.success) {
        setMessage('Senha redefinida com sucesso! Redirecionando para o login...');
        setIsError(false);

        // Limpar qualquer token/dados antigos do localStorage
        removeToken();

        // Redirecionar para login após 3 segundos
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setMessage(response.message || 'Erro ao redefinir senha. Tente novamente.');
        setIsError(true);
      }
    } catch (error: any) {
      console.error('Erro ao redefinir senha:', error);
      
      let errorMessage = 'Erro ao redefinir senha. Tente novamente.';
      
      if (error.message && error.message.includes('400')) {
        errorMessage = 'Token inválido ou expirado. Solicite uma nova redefinição de senha.';
      } else if (error.message && error.message.includes('404')) {
        errorMessage = 'Token não encontrado. Solicite uma nova redefinição de senha.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setMessage(errorMessage);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  if (!tokenValid) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-container">
          <div className="logo-section">
            <div className="logo">
              <img 
                src="https://a.imagem.app/3bTgpi.png" 
                alt="Logo ROBOT.IA" 
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  if (target.parentElement) {
                    target.parentElement.innerHTML = 'ROBOT.IA';
                  }
                }}
              />
            </div>
            <h1 className="logo-text">ROBOT.IA</h1>
            <p className="logo-subtitle">Checklist de Veículos e Máquinas</p>
          </div>

          <div className="error-message" style={{ display: 'block' }}>
            {message}
          </div>

          <div className="back-to-login">
            <button 
              type="button" 
              onClick={() => navigate('/login')}
              className="back-button"
            >
              Voltar para o Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <div className="reset-password-container">
        <div className="logo-section">
          <div className="logo">
            <img 
              src="https://a.imagem.app/3bTgpi.png" 
              alt="Logo ROBOT.IA" 
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                if (target.parentElement) {
                  target.parentElement.innerHTML = 'ROBOT.IA';
                }
              }}
            />
          </div>
          <h1 className="logo-text">ROBOT.IA</h1>
          <p className="logo-subtitle">Redefinir Senha</p>
        </div>

        {message && (
          <div className={`message ${isError ? 'error' : 'success'}`}>
            {message}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="password">Nova Senha *</label>
            <div className="input-wrapper">
              <input 
                type="password" 
                id="password" 
                name="password" 
                placeholder="Mínimo 8 caracteres com letras e números"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />
              <span className="input-icon">🔒</span>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirmar Nova Senha *</label>
            <div className="input-wrapper">
              <input 
                type="password" 
                id="confirmPassword" 
                name="confirmPassword" 
                placeholder="Digite a senha novamente"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isLoading}
              />
              <span className="input-icon">🔒</span>
            </div>
          </div>

          <button 
            type="submit" 
            className="submit-btn" 
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <LoadingSpinner />
                Redefinindo...
              </>
            ) : (
              'Redefinir Senha'
            )}
          </button>
        </form>

        <div className="back-to-login">
          <button 
            type="button" 
            onClick={() => navigate('/login')}
            className="back-button"
            disabled={isLoading}
          >
            Voltar para o Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
